/**
 * Getypeerde toegang tot `/tasks`: schoonmaak, onderhoud en inspectie.
 *
 * Wat hier dragend is, is de datum. Een taak met de verkeerde dag is erger dan
 * geen taak: er staat dan iets op de lijst dat vals vertrouwen geeft. Een datum
 * die niet als `YYYY-MM-DD` binnenkomt, laat de taak daarom vallen in plaats van
 * hem met een lege of geraden dag te tonen.
 */
import { call, isRecord, readOptionalText, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type TaskFailure = Failure
export type TaskResult<T> = Result<T>

export const TASK_KINDS = [
  'cleaning',
  'maintenance',
  'inspection',
  // Uit migratie 0026: een meldplicht wordt een taak (§17.28). Ontbrak hier,
  // waardoor de takenlijst hem stilzwijgend oversloeg.
  'compliance',
  'other',
] as const
export type TaskKind = (typeof TASK_KINDS)[number]

export const TASK_STATUSES = ['open', 'done', 'cancelled'] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]

export interface Task {
  readonly id: string
  readonly residenceId: string
  readonly stayId: string | null
  readonly kind: TaskKind
  readonly title: string
  readonly notes: string
  readonly dueOn: string
  readonly status: TaskStatus
  readonly assignedTo: string | null
  readonly assignedToName: string
  readonly completedAt: string | null
}

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/

function isKind(value: unknown): value is TaskKind {
  const known: readonly string[] = TASK_KINDS
  return typeof value === 'string' && known.includes(value)
}

function isStatus(value: unknown): value is TaskStatus {
  const known: readonly string[] = TASK_STATUSES
  return typeof value === 'string' && known.includes(value)
}

function readTask(value: unknown): Task | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const dueOn = readText(value, 'dueOn')
  const kind = value['kind']
  const status = value['status']
  if (id === '' || !CALENDAR_DATE.test(dueOn) || !isKind(kind) || !isStatus(status)) {
    return null
  }

  return {
    id,
    dueOn,
    kind,
    status,
    residenceId: readText(value, 'residenceId'),
    stayId: readOptionalText(value, 'stayId'),
    title: readText(value, 'title'),
    notes: readText(value, 'notes'),
    assignedTo: readOptionalText(value, 'assignedTo'),
    assignedToName: readText(value, 'assignedToName'),
    completedAt: readOptionalText(value, 'completedAt'),
  }
}

export interface TaskQuery {
  readonly residenceId?: string
  readonly status?: TaskStatus
  readonly until?: string
}

export async function listTasks(
  query: TaskQuery = {},
  options: RequestOptions = {},
): Promise<TaskResult<readonly Task[]>> {
  const search = new URLSearchParams()
  if (query.residenceId !== undefined) {
    search.set('residenceId', query.residenceId)
  }
  if (query.status !== undefined) {
    search.set('status', query.status)
  }
  if (query.until !== undefined) {
    search.set('until', query.until)
  }
  const suffix = search.toString() === '' ? '' : `?${search.toString()}`

  return call(
    'GET',
    `/tasks${suffix}`,
    undefined,
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['tasks'])) {
        return null
      }
      const tasks: Task[] = []
      for (const entry of payload['tasks']) {
        const task = readTask(entry)
        if (task !== null) {
          tasks.push(task)
        }
      }
      return tasks
    },
    options,
  )
}

export interface CreateTaskBody {
  readonly residenceId: string
  readonly kind: TaskKind
  readonly title: string
  readonly notes: string
  readonly dueOn: string
  readonly stayId?: string | null
}

function readOneTask(payload: unknown): Task | null {
  return isRecord(payload) ? readTask(payload['task']) : null
}

export async function createTask(body: CreateTaskBody): Promise<TaskResult<Task>> {
  return call('POST', '/tasks', body, readOneTask)
}

export type TaskPatch = Partial<{
  title: string
  notes: string
  dueOn: string
  kind: TaskKind
  status: TaskStatus
  assignedTo: string | null
}>

export async function updateTask(id: string, patch: TaskPatch): Promise<TaskResult<Task>> {
  return call('PATCH', `/tasks/${encodeURIComponent(id)}`, patch, readOneTask)
}

export async function deleteTask(id: string): Promise<TaskResult<true>> {
  return call('DELETE', `/tasks/${encodeURIComponent(id)}`, undefined, () => true)
}

/** Vertaalsleutel bij een mislukte aanroep. */
export function taskErrorMessageKey(failure: TaskFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.tasks.error.rejected'
    case 'not_found':
      return 'dashboard.tasks.error.notFound'
    case 'forbidden':
      return 'dashboard.tasks.error.forbidden'
    case 'rate_limited':
      return 'dashboard.tasks.error.rateLimited'
    default:
      return 'dashboard.tasks.error.unavailable'
  }
}

/** Vandaag als `YYYY-MM-DD`, in de tijdzone van de browser. */
export function today(): string {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return local.toISOString().slice(0, 10)
}
